import type { SearchForMatchingUsersInput, UserCountOptions, UserCreateDto, UserDto, UserProfileDto, UserQueryOptions, UserUpdateDto } from './models';
import { RestService } from '@abp/ng.core';
import type { PagedResultDto } from '@abp/ng.core';
import { Injectable } from '@angular/core';
import type { EnumDto, PagedResultWithStatsDto } from '../common/models';
import type { MediaInfoDto } from '../files/models';
import type { IFormFile } from '../microsoft/asp-net-core/http/models';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  apiName = 'Default';

  create = (input: UserCreateDto, asset?: IFormFile, thumb?: IFormFile, cancellationToken?: any) =>
    this.restService.request<any, UserDto>({
      method: 'POST',
      url: `/api/app/user`,
    },
    { apiName: this.apiName });

  createWithNoMedia = (input: UserCreateDto, cancellationToken?: any) =>
    this.restService.request<any, UserDto>({
      method: 'POST',
      url: `/api/app/user/no-media`,
      body: input,
    },
    { apiName: this.apiName });

  delete = (id: string, cancellationToken?: any) =>
    this.restService.request<any, void>({
      method: 'DELETE',
      url: `/api/app/user/${id}`,
    },
    { apiName: this.apiName });

  disable = (id: string, cancellationToken?: any) =>
    this.restService.request<any, void>({
      method: 'POST',
      url: `/api/app/user/disable/${id}`,
    },
    { apiName: this.apiName });

  get = (id: string, cancellationToken?: any) =>
    this.restService.request<any, UserDto>({
      method: 'GET',
      url: `/api/app/user/${id}`,
    },
    { apiName: this.apiName });

  getActivityTypes = () =>
    this.restService.request<any, EnumDto[]>({
      method: 'GET',
      url: `/api/app/lookups/user-activity-types`,
    },
    { apiName: this.apiName });

  getCount = (options?: UserCountOptions, cancellationToken?: any) =>
    this.restService.request<any, number>({
      method: 'GET',
      url: `/api/app/users/count`,
      params: { searchText: options?.searchText, filterStatus: options?.filterStatus, filterName: options?.filterName, filterPhoneNumber: options?.filterPhoneNumber, filterEmail: options?.filterEmail, filterIsArtist: options?.filterIsArtist, filterIsAnonymous: options?.filterIsAnonymous },
    },
    { apiName: this.apiName });

  getList = (options?: UserQueryOptions, cancellationToken?: any) =>
    this.restService.request<any, PagedResultDto<UserDto>>({
      method: 'GET',
      url: `/api/app/users`,
      params: { searchText: options?.searchText, filterStatus: options?.filterStatus, filterName: options?.filterName, filterPhoneNumber: options?.filterPhoneNumber, filterEmail: options?.filterEmail, filterIsArtist: options?.filterIsArtist, filterIsAnonymous: options?.filterIsAnonymous, filterIsAppleAPN: options?.filterIsAppleAPN, filterIsAbpUser: options?.filterIsAbpUser, maxResultCount: options?.maxResultCount, sorting: options?.sorting, skipCount: options?.skipCount },
    },
    { apiName: this.apiName });

  getListWithStats = (options: UserQueryOptions, cancellationToken?: any) =>
    this.restService.request<any, PagedResultWithStatsDto<UserDto>>({
      method: 'GET',
      url: `/api/app/users/with-stats`,
      params: { searchText: options?.searchText, filterStatus: options?.filterStatus, filterName: options?.filterName, filterPhoneNumber: options?.filterPhoneNumber, filterEmail: options?.filterEmail, filterIsArtist: options?.filterIsArtist, filterIsAnonymous: options?.filterIsAnonymous, maxResultCount: options?.maxResultCount, sorting: options?.sorting, skipCount: options?.skipCount },
    },
    { apiName: this.apiName });

  getUserStatuses = () =>
    this.restService.request<any, EnumDto[]>({
      method: 'GET',
      url: `/api/app/lookups/user-statuses`,
    },
    { apiName: this.apiName });

  oldGetUserProfileById = (id: string) =>
    this.restService.request<any, UserProfileDto>({
      method: 'GET',
      url: `/api/app/users/old/user-profile`,
      params: { id: id },
    },
    { apiName: this.apiName });

  oldQueryByFilterStringByFilterString = (filterString: string) =>
    this.restService.request<any, UserDto[]>({
      method: 'GET',
      url: `/api/app/users/old/by-filter-string`,
      params: { filterString: filterString },
    },
    { apiName: this.apiName });

  oldSearchForMatchingUsersByInputAndCancellationToken = (input: SearchForMatchingUsersInput, cancellationToken: any) =>
    this.restService.request<any, UserDto[]>({
      method: 'GET',
      url: `/api/app/users/old/matching-users`,
      params: { filterText: input.filterText, maxResultCount: input.maxResultCount, sorting: input.sorting, skipCount: input.skipCount },
    },
    { apiName: this.apiName });

  update = (id: string, input: UserUpdateDto, asset?: IFormFile, thumb?: IFormFile, cancellationToken?: any) =>
    this.restService.request<any, UserDto>({
      method: 'PUT',
      url: `/api/app/user/${id}`,
    },
    { apiName: this.apiName });

  updateMediaAsset = (id: string, canLoop: boolean, autoCreateThumbnail: boolean, file: IFormFile, cancellationToken?: any) =>
    this.restService.request<any, MediaInfoDto>({
      method: 'PUT',
      url: `/api/app/user/${id}/media/asset`,
      params: { canLoop: canLoop, autoCreateThumbnail: autoCreateThumbnail },
    },
    { apiName: this.apiName });

  updateMediaThumb = (id: string, canLoop: boolean, file: IFormFile, cancellationToken?: any) =>
    this.restService.request<any, MediaInfoDto>({
      method: 'PUT',
      url: `/api/app/user/${id}/media/thumbnail`,
      params: { canLoop: canLoop },
    },
    { apiName: this.apiName });

  updateWithNoMedia = (id: string, input: UserUpdateDto, cancellationToken?: any) =>
    this.restService.request<any, UserDto>({
      method: 'PUT',
      url: `/api/app/user/${id}/no-media`,
      body: input,
    },
    { apiName: this.apiName });

  constructor(private restService: RestService) {}
}
